
import React, { useEffect, useState } from "react";
import { API } from "./Config";

function VerifyEmail({ setPage }) {
  const [status, setStatus] = useState("Verifying your email...");
  const [verificationState, setVerificationState] = useState("verifying");
  const [email, setEmail] = useState("");
  const [nextResendAt, setNextResendAt] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  const [statusChecked, setStatusChecked] = useState(false);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token");

    if (!token) {
      setVerificationState("failed");
      setStatus("Verification link is missing its token.");
      return;
    }

    let cancelled = false;

    fetch(`${API}/verify-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token })
    })
      .then(async (res) => {
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || "Email verification failed.");
        }

        return data;
      })
      .then((data) => {
        if (cancelled) return;

        setVerificationState("verified");

        setStatus(
          data.alreadyVerified
            ? "Your email has already been verified. You can now log in."
            : "Email verified successfully!"
        );

        setNextResendAt(null);
        setCanResend(false);
      })


      .catch(async (err) => {
        if (cancelled) return;

        const tokenError =
        err.message || "This verification link is invalid or has expired.";

        setVerificationState("failed");
        setStatus(tokenError);

        // For a reused or expired link, let the user
        // check their account status using their email.
        setStatusChecked(false);
        setCanResend(false);
        setNextResendAt(null);
    });



    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!nextResendAt) {
      setSecondsLeft(0);
      return;
    }

    const updateCountdown = () => {
      const remaining = Math.max(
        0,
        Math.ceil((new Date(nextResendAt).getTime() - Date.now()) / 1000)
      );

      setSecondsLeft(remaining);

      if (remaining === 0 && statusChecked) {
        setCanResend(true);
      }
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);

    return () => clearInterval(timer);
  }, [nextResendAt, statusChecked]);

  const checkStatus = async () => {
    if (!email.trim()) {
      alert("Please enter your email address.");
      return;
    }

    setChecking(true);
    setStatusChecked(false);
    setCanResend(false);

    try {
      const res = await fetch(`${API}/verification-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Unable to check resend status.");
      }

      setStatusChecked(true);

      if (!data.verificationRequired) {
        setNextResendAt(null);
        setCanResend(false);
        setVerificationState("verified");
        setStatus(
          "No further email verification is required for this address. If you have an account, you can proceed to login."
        );
        return;
      }

      setVerificationState("failed");
      setNextResendAt(data.nextResendAt || null);

      const remaining = data.retryAfterSeconds || 0;
      setCanResend(data.canResend === true || remaining === 0);

      if (remaining > 0) {
        setStatus("Your email is not verified yet. Please wait until the next available resend time.");
      } else {
        setStatus("Your email is not verified yet. You can request a verification email now.");
      }
    } catch (err) {
      setStatus(err.message || "Unable to check verification status.");
    } finally {
      setChecking(false);
    }
  };

  const resendEmail = async () => {
    if (!email.trim()) {
      alert("Please enter your email address.");
      return;
    }

    setSending(true);
    setCanResend(false);

    try {
      const res = await fetch(`${API}/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() })
      });

      const data = await res.json();

      if (res.status === 429) {
        setStatus(data.error || "Please wait before trying again.");

        if (data.retryAfterSeconds > 0) {
          setNextResendAt(
            new Date(Date.now() + data.retryAfterSeconds * 1000).toISOString()
          );
          setStatusChecked(true);
        } else {
          setStatusChecked(false);
        }

        return;
      }

      if (!res.ok) {
        throw new Error(data.error || "Unable to send verification email.");
      }

      setStatus(
        data.message ||
        "If your account needs verification, a verification email will be sent."
      );

      await checkStatus();
    } catch (err) {
      setStatus(err.message || "Unable to send verification email.");
      setStatusChecked(false);
    } finally {
      setSending(false);
    }
  };

  const formatCountdown = (totalSeconds) => {
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (days > 0) return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    return `${minutes}m ${seconds}s`;
  };

  const buttonStyle = {
    width: "100%",
    padding: 10,
    background: "#2c3e50",
    color: "white",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    marginTop: 10
  };

  const goToLogin = () => {
    window.history.replaceState({}, "", "/");
    setPage("login");
  };

  return (
    <div
      style={{
        minHeight: "80vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#f5f6fa",
        padding: 20
      }}
    >
      <div
        style={{
          background: "white",
          padding: 30,
          borderRadius: 10,
          boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
          width: "100%",
          maxWidth: 420,
          boxSizing: "border-box"
        }}
      >
        <h2>Email Verification</h2>
        <p>{status}</p>

        {verificationState === "verified" ? (
          <button
            onClick={goToLogin}
            style={{ ...buttonStyle, background: "#2563eb" }}
          >
            Back to Login
          </button>
        ) : (
          <>
            <hr
              style={{
                margin: "20px 0",
                border: "none",
                borderTop: "1px solid #eee"
              }}
            />

            <h3>Need a verification email?</h3>
            <p style={{ fontSize: 13, color: "#666" }}>
              Enter your MukBi account email to check whether another
              verification email is available.
            </p>

            <input
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setStatusChecked(false);
                setCanResend(false);
                setNextResendAt(null);
              }}
              style={{
                width: "100%",
                padding: 10,
                margin: "10px 0",
                borderRadius: 6,
                border: "1px solid #ccc",
                boxSizing: "border-box"
              }}
            />

            <button
              onClick={checkStatus}
              disabled={checking || sending}
              style={buttonStyle}
            >
              {checking ? "Checking..." : "Check next available time"}
            </button>

            {statusChecked && nextResendAt && secondsLeft > 0 && (
              <div
                style={{
                  marginTop: 15,
                  padding: 12,
                  background: "#f5f6fa",
                  borderRadius: 6
                }}
              >
                <strong>Next resend available:</strong>
                <div>{new Date(nextResendAt).toLocaleString()}</div>
                <p>Time remaining: {formatCountdown(secondsLeft)}</p>
              </div>
            )}

            {statusChecked && canResend && (
              <div
                style={{
                  marginTop: 15,
                  padding: 12,
                  background: "#f5f6fa",
                  borderRadius: 6
                }}
              >
                You can request another verification email now.
              </div>
            )}

            <button
              onClick={resendEmail}
              disabled={
                sending ||
                checking ||
                !statusChecked ||
                !canResend ||
                secondsLeft > 0
              }
              style={{
                ...buttonStyle,
                background:
                  sending ||
                  checking ||
                  !statusChecked ||
                  !canResend ||
                  secondsLeft > 0
                    ? "#9ca3af"
                    : "#2563eb",
                cursor:
                  sending ||
                  checking ||
                  !statusChecked ||
                  !canResend ||
                  secondsLeft > 0
                    ? "not-allowed"
                    : "pointer"
              }}
            >
              {sending ? "Sending..." : "Resend verification email"}
            </button>

            <button
              onClick={goToLogin}
              style={{ ...buttonStyle, background: "#6b7280" }}
            >
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default VerifyEmail;
